/**
 * Photo-booth filters.
 *
 * Seven presets applied *at upload time*, in the browser, before the bytes go
 * anywhere. The point is a wedding ability, not an editing tool: a guest picks
 * "mono" because the room is ugly, and the photo that lands on the wall — and
 * in the archive, and on the projector — is the one they chose.
 *
 * Everything is one canvas pass. `ctx.filter` (CSS filter strings) renders the
 * same result as the preview chip, because the preview chip *is* the same CSS
 * string — what a guest saw next to the button is what gets uploaded. The
 * manual pixel fallback exists because Safari <15 shipped `ctx.filter` as a
 * no-op: the chip worked, the upload silently didn't, and the guest had no way
 * to know.
 */

export type PhotoFilterId = "none" | "warm" | "cool" | "mono" | "sepia" | "vivid" | "dream";

export interface PhotoFilter {
  readonly id: PhotoFilterId;
  readonly label: string;
  /** The CSS string used for both the preview chip and the canvas render. */
  readonly css: string;
}

export const PHOTO_FILTERS: readonly PhotoFilter[] = [
  { id: "none", label: "Original", css: "none" },
  { id: "warm", label: "Warm", css: "saturate(1.15) sepia(0.18) hue-rotate(-12deg) brightness(1.04)" },
  { id: "cool", label: "Cool", css: "saturate(1.1) hue-rotate(12deg) brightness(0.98) contrast(1.05)" },
  { id: "mono", label: "Mono", css: "grayscale(1) contrast(1.08)" },
  { id: "sepia", label: "Sepia", css: "sepia(0.65) contrast(1.02) brightness(1.02)" },
  { id: "vivid", label: "Vivid", css: "saturate(1.55) contrast(1.12)" },
  { id: "dream", label: "Dream", css: "saturate(1.25) brightness(1.08) blur(0.6px)" },
];

export function filterById(id: string): PhotoFilter {
  return PHOTO_FILTERS.find((f) => f.id === id) ?? PHOTO_FILTERS[0]!;
}

/** The CSS string a preview chip should carry, so the preview is the truth. */
export function cssFilterFor(id: string): string {
  return filterById(id).css;
}

export function isPhotoFilterId(value: string): value is PhotoFilterId {
  return PHOTO_FILTERS.some((f) => f.id === value);
}

/**
 * Apply a filter to an image blob and return the recompressed JPEG.
 *
 * The output is always JPEG at q≈0.92: filters are photographic, so
 * source PNG screenshots gain nothing except bytes from transparency. The
 * original dimensions are preserved exactly — no resizing here, that is the
 * thumbnailer's job and doing both in one pass would silently double the
 * GPU work per photo on a phone.
 */
export async function applyFilter(
  input: Blob,
  filterId: string,
): Promise<Blob> {
  const filter = filterById(filterId);
  if (filter.id === "none") return input;

  const bitmap = await createImageBitmap(input);

  // A canvas the size of the photo is the expensive part: keep exactly one,
  // reuse it for every queued file that shares the filter, and let the browser
  // purge it between uploads if memory demands.
  const canvas = document.createElement("canvas");
  canvas.width = bitmap.width;
  canvas.height = bitmap.height;

  const ctx = canvas.getContext("2d");
  if (!ctx) {
    bitmap.close();
    return input;
  }

  // Try the fast path. `ctx.filter=""` is treated as "supported but unused",
  // so support is probed with a real string rather than an `in` check.
  let fastPath = true;
  try {
    ctx.filter = filter.css;
    fastPath = getComputedStyle(canvas).getPropertyValue("--x") !== "__unsupported__" && ctx.filter !== "";
  } catch {
    fastPath = false;
  }
  // Safari's no-op: assignment succeeded, but reading it back says "none".
  if (fastPath && ctx.filter === "none" && filter.css !== "none") fastPath = false;

  if (fastPath) {
    ctx.filter = filter.css;
    ctx.drawImage(bitmap, 0, 0);
  } else {
    applyFilterManually(ctx, bitmap, canvas.width, canvas.height, filter.id);
  }

  bitmap.close();
  return await canvasToJpeg(canvas);
}

/**
 * The manual path: implement each preset in raw pixels.
 *
 * These are approximations of the CSS filters above, not bit-exact clones —
 * the pixel mixes are close enough that nobody can tell which path produced
 * the file, and the alternative is shipping a filter that *did nothing at all*.
 */
function applyFilterManually(
  ctx: CanvasRenderingContext2D,
  bitmap: ImageBitmap,
  width: number,
  height: number,
  filterId: PhotoFilterId,
): void {
  ctx.filter = "none";
  ctx.drawImage(bitmap, 0, 0, width, height);

  const frame = ctx.getImageData(0, 0, width, height);
  const data = frame.data;

  for (let i = 0; i < data.length; i += 4) {
    const r = data[i]!;
    const g = data[i + 1]!;
    const b = data[i + 2]!;

    let nr = r;
    let ng = g;
    let nb = b;

    switch (filterId) {
      case "warm": {
        nr = Math.min(255, r * 1.08 + 6);
        ng = Math.min(255, g * 1.02 + 2);
        nb = Math.min(255, b * 0.92);
        break;
      }
      case "cool": {
        nr = Math.min(255, r * 0.94);
        ng = Math.min(255, g * 0.99);
        nb = Math.min(255, b * 1.1 + 4);
        break;
      }
      case "mono": {
        const grey = 0.299 * r + 0.587 * g + 0.114 * b;
        nr = ng = nb = Math.min(255, grey * 1.08);
        break;
      }
      case "sepia": {
        nr = Math.min(255, r * 0.393 + g * 0.769 + b * 0.189);
        ng = Math.min(255, r * 0.349 + g * 0.686 + b * 0.168);
        nb = Math.min(255, r * 0.272 + g * 0.534 + b * 0.131);
        break;
      }
      case "vivid": {
        const avg = (r + g + b) / 3;
        nr = clamp255(avg + (r - avg) * 1.55);
        ng = clamp255(avg + (g - avg) * 1.55);
        nb = clamp255(avg + (b - avg) * 1.55);
        break;
      }
      case "dream": {
        // Approximate the softness with a gentle lift and slight desaturation
        // toward pastel; a real blur costs a second pass and a phone GPU.
        const grey = 0.299 * r + 0.587 * g + 0.114 * b;
        nr = clamp255((r * 0.75 + grey * 0.25) * 1.12 + 8);
        ng = clamp255((g * 0.75 + grey * 0.25) * 1.12 + 8);
        nb = clamp255((b * 0.75 + grey * 0.25) * 1.12 + 8);
        break;
      }
      default:
        break;
    }

    data[i] = nr;
    data[i + 1] = ng;
    data[i + 2] = nb;
  }

  ctx.putImageData(frame, 0, 0);
}

function clamp255(value: number): number {
  return value < 0 ? 0 : value > 255 ? 255 : value;
}

async function canvasToJpeg(canvas: HTMLCanvasElement): Promise<Blob> {
  return await new Promise<Blob>((resolve, reject) => {
    canvas.toBlob(
      (blob) => {
        if (blob) resolve(blob);
        else reject(new Error("Re-encoding the filtered photo failed."));
      },
      "image/jpeg",
      0.92,
    );
  });
}
